import { StudentProgress, SectionProgress, Section } from '../types';

const STORAGE_KEY = 'quiz-progress';

// Generate a GUID for unique test identification
const generateGUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

export const saveProgress = (progress: StudentProgress): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
  } catch (error) {
    console.error('Failed to save progress:', error);
  }
};

export const loadProgress = (): StudentProgress | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const progress = JSON.parse(stored);
    // Convert date strings back to Date objects
    progress.startedAt = new Date(progress.startedAt);
    if (progress.completedAt) {
      progress.completedAt = new Date(progress.completedAt);
    }

    progress.sections.forEach((section: SectionProgress) => {
      if (section.startedAt) {
        section.startedAt = new Date(section.startedAt);
      }
      if (section.completedAt) {
        section.completedAt = new Date(section.completedAt);
      }
      // Ensure answers array exists
      if (!section.answers) {
        section.answers = [];
      }
    });

    // Add missing fields for backwards compatibility
    if (progress.currentQuestion === undefined) {
      progress.currentQuestion = 0;
    }
    if (!progress.firstName) {
      progress.firstName = '';
    }
    if (!progress.lastName) {
      progress.lastName = '';
    }
    if (!progress.studentId) {
      progress.studentId = '';
    }
    if (!progress.testId) {
      progress.testId = generateGUID();
    }

    return progress;
  } catch (error) {
    console.error('Failed to load progress:', error);
    return null;
  }
};

export const initializeProgress = (
  studentInfo: { firstName: string; lastName: string; email: string; studentId: string },
  sections: Section[]
): StudentProgress => {
  const sectionProgress: SectionProgress[] = sections.map(section => ({
    id: section.id,
    name: section.name,
    completed: false,
    score: 0,
    attempts: 0,
    answers: [],
    passed: false,
  }));

  return {
    email: studentInfo.email,
    firstName: studentInfo.firstName,
    lastName: studentInfo.lastName,
    studentId: studentInfo.studentId,
    testId: generateGUID(),
    currentSection: 0,
    sections: sectionProgress,
    startedAt: new Date(),
    overallScore: 0,
    certificateGenerated: false,
  };
};

export const clearProgress = (): void => {
  localStorage.removeItem(STORAGE_KEY);
};